import { createCalcMissionSession } from './calcMission';
import { DEMO_PROJECT_ID } from './demoWorkspace';
import { getActiveProjects } from './projects';
import { buildRestoredProfile, createDefaultProfile } from './profile';
import { createDefaultWorkspace } from '@shared/storage';
import { CalcMissionSession, DealRoom, ProjectContext, TreasuryProfile, UserProfile, Workspace } from '@shared/types';

const OWNER_PRESETS_KEY = 'indxone.owner.presets.v1';
const PROFILE_KEY = 'indxone.profile.v1';
const MISSIO_OWNER_BACKUP_KEY = 'indxone.missio.owner.backup.v1';

interface OwnerWorkspacePreset {
  version: 1;
  body: string;
  updatedAt: number;
  workspace: Pick<
    Workspace,
    'profile' | 'projects' | 'dealRooms' | 'treasuries' | 'calcMissionSessions' | 'currentProjectId' | 'currentCalcMissionSessionId'
  >;
}

type OwnerPresetRegistry = Record<string, OwnerWorkspacePreset>;

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore local storage write failures
  }
}

function cloneWorkspaceSubset(workspace: Workspace): OwnerWorkspacePreset['workspace'] {
  return {
    profile: { ...workspace.profile },
    projects: workspace.projects.map((project) => ({ ...project })),
    dealRooms: workspace.dealRooms.map((dealRoom) => ({ ...dealRoom, tasks: dealRoom.tasks.map((task) => ({ ...task })) })),
    treasuries: workspace.treasuries.map((treasury) => ({
      ...treasury,
      charges: treasury.charges.map((charge) => ({ ...charge })),
    })),
    calcMissionSessions: workspace.calcMissionSessions.map((session) => ({
      ...session,
      simple: { ...session.simple },
    })),
    currentProjectId: workspace.currentProjectId,
    currentCalcMissionSessionId: workspace.currentCalcMissionSessionId,
  };
}

function mergeById<T extends { id: string }>(current: T[], incoming: T[]): T[] {
  const map = new Map<string, T>();
  incoming.forEach((item) => map.set(item.id, item));
  current.forEach((item) => map.set(item.id, item));
  return Array.from(map.values());
}

function isDemoOnlyWorkspace(workspace: Workspace) {
  const activeProjects = getActiveProjects(workspace.projects);
  return activeProjects.length === 1 && activeProjects[0]?.id === DEMO_PROJECT_ID;
}

function isBlank(value: unknown) {
  return value === undefined || value === null || value === '' || value === 0;
}

function mergeProfile(current: UserProfile, incoming: UserProfile): UserProfile {
  const defaultProfile = createDefaultProfile(incoming.updatedAt || Date.now());
  const next = { ...current };

  (Object.keys(incoming) as Array<keyof UserProfile>).forEach((key) => {
    const currentValue = current[key];
    const incomingValue = incoming[key];
    const defaultValue = defaultProfile[key];

    if (Array.isArray(incomingValue)) return;
    if (incomingValue === undefined || incomingValue === null || incomingValue === '') return;

    // Never override an explicitly user-controlled enum field (status) just because it matches the default
    if (key === 'status') return;

    const shouldReplace =
      isBlank(currentValue) ||
      currentValue === defaultValue;

    if (shouldReplace) {
      next[key] = incomingValue as never;
    }
  });

  next.updatedAt = Date.now();
  return next;
}

function buildOwnerProject(profile: UserProfile): ProjectContext {
  const now = Date.now();
  const displayName = `${profile.firstName} ${profile.lastName}`.trim() || profile.company || 'Mon activite';
  return {
    id: `owner-project-${profile.id || 'default'}`,
    name: profile.company ? `Activite - ${profile.company}` : 'Mon activite',
    client: profile.company || displayName,
    owner: displayName,
    description: profile.headline || 'Projet central derive de votre profil owner.',
    scope: 'Base de travail pour vos calculs, votre resume client et votre pilotage.',
    budget: profile.tjm * Math.max(profile.daysPerMonth, 1),
    tjm: profile.tjm,
    currency: 'EUR',
    createdAt: now,
    updatedAt: now,
  };
}

function buildOwnerDealRoom(profile: UserProfile, project: ProjectContext): DealRoom {
  const now = Date.now();
  return {
    id: `owner-deal-room-${project.id}`,
    projectId: project.id,
    name: project.name,
    client: project.client,
    owner: project.owner,
    status: 'cadre',
    objective: profile.headline || 'Presenter clairement votre activite et vos offres.',
    context: profile.notes || 'Base de presentation issue de votre profil owner.',
    scope: project.scope,
    deliverables: ['Partage client', 'Presentation de l activite', 'Repères de collaboration'],
    tasks: [
      { id: `task-${project.id}-1`, title: 'Verifier le profil', status: 'done' },
      { id: `task-${project.id}-2`, title: 'Ajuster le partage client', status: 'todo' },
    ],
    milestones: [],
    risks: [],
    decisions: ['Utiliser le profil owner comme socle de reprise.'],
    createdAt: now,
    updatedAt: now,
  };
}

function buildOwnerTreasury(profile: UserProfile, project: ProjectContext): TreasuryProfile {
  const now = Date.now();
  return {
    id: `owner-treasury-${project.id}`,
    projectId: project.id,
    status: profile.status,
    openingBalance: 5000,
    caYtd: 0,
    horizonMonths: 6,
    daysPerMonth: profile.daysPerMonth,
    tjm: profile.tjm,
    charges: [],
    taxStrategy: profile.status === 'sasu' ? 'dividendes' : profile.status === 'portage' ? 'remuneration' : 'mixte',
    salaryNetTarget: profile.netTarget || 0,
    dividendPayoutPct: 100,
    vatRegistered: false,
    createdAt: now,
    updatedAt: now,
  };
}

function buildOwnerCalcSession(profile: UserProfile, project: ProjectContext): CalcMissionSession {
  return {
    ...createCalcMissionSession({
      name: project.name,
      projectId: project.id,
      profile,
      templateKey: 'regie-simple',
    }),
    simple: {
      name: project.name,
      client: project.client,
      tjm: profile.tjm,
      days: profile.daysPerMonth,
      fixedCosts: 300,
      variableCosts: 120,
      extraCosts: 0,
      netTarget: profile.netTarget ?? 2500,
      successRate: 100,
      contingencyPct: 10,
      status: profile.status,
    },
  };
}

export function getOwnerLicenseBody(input?: { kind?: string | null; body?: string | null; isPro?: boolean } | null) {
  if (!input || input.kind !== 'owner' || !input.body || input.isPro === false) return null;
  return input.body;
}

export function readOwnerWorkspacePreset(body: string): OwnerWorkspacePreset | null {
  const registry = readJson<OwnerPresetRegistry>(OWNER_PRESETS_KEY) || {};
  return registry[body] || null;
}

export function writeOwnerWorkspacePreset(body: string, workspace: Workspace) {
  const registry = readJson<OwnerPresetRegistry>(OWNER_PRESETS_KEY) || {};
  const profile = buildRestoredProfile(workspace.profile);
  const nextPreset: OwnerWorkspacePreset = {
    version: 1,
    body,
    updatedAt: Date.now(),
    workspace: cloneWorkspaceSubset({
      ...workspace,
      profile,
    }),
  };

  registry[body] = nextPreset;
  writeJson(OWNER_PRESETS_KEY, registry);
  writeJson(PROFILE_KEY, profile);
  writeJson(MISSIO_OWNER_BACKUP_KEY, {
    prenom: profile.firstName,
    nom: profile.lastName,
    email: profile.email,
    societe: profile.company,
    role: profile.role,
    tjm: profile.tjm,
    jours: profile.daysPerMonth,
    status: profile.status,
    netTarget: profile.netTarget,
  });
}

export function hydrateWorkspaceFromOwnerPreset(workspace: Workspace, body: string): Workspace {
  const preset = readOwnerWorkspacePreset(body);
  const restoredProfile = buildRestoredProfile(workspace.profile);
  const base = createDefaultWorkspace();

  if (!preset) {
    const activeProjects = getActiveProjects(workspace.projects);
    const fallbackProject = activeProjects[0] || buildOwnerProject(restoredProfile);
    const fallbackDealRoom = workspace.dealRooms.find((item) => item.projectId === fallbackProject.id) || buildOwnerDealRoom(restoredProfile, fallbackProject);
    const fallbackTreasury = workspace.treasuries.find((item) => item.projectId === fallbackProject.id) || buildOwnerTreasury(restoredProfile, fallbackProject);
    const fallbackCalc = workspace.calcMissionSessions.find((item) => item.projectId === fallbackProject.id) || buildOwnerCalcSession(restoredProfile, fallbackProject);

    return {
      ...workspace,
      profile: mergeProfile(workspace.profile, restoredProfile),
      projects: activeProjects.length > 0 ? workspace.projects : [...workspace.projects, fallbackProject],
      dealRooms: workspace.dealRooms.length > 0 ? workspace.dealRooms : [fallbackDealRoom],
      treasuries: workspace.treasuries.length > 0 ? workspace.treasuries : [fallbackTreasury],
      calcMissionSessions: workspace.calcMissionSessions.length > 0 ? workspace.calcMissionSessions : [fallbackCalc],
      currentProjectId: !workspace.currentProjectId || isDemoOnlyWorkspace(workspace) ? fallbackProject.id : workspace.currentProjectId,
      currentCalcMissionSessionId:
        !workspace.currentCalcMissionSessionId || isDemoOnlyWorkspace(workspace)
          ? fallbackCalc.id
          : workspace.currentCalcMissionSessionId,
      meta: {
        ...workspace.meta,
        updatedAt: Date.now(),
        migratedFrom: Array.from(new Set([...(workspace.meta.migratedFrom || []), 'owner-profile-fallback'])),
      },
    };
  }

  const incoming = preset.workspace;
  const mergedProjects = mergeById(workspace.projects, incoming.projects);
  const activeMergedProjects = getActiveProjects(mergedProjects);
  const mergedDealRooms = mergeById(workspace.dealRooms, incoming.dealRooms);
  const mergedTreasuries = mergeById(workspace.treasuries, incoming.treasuries);
  const mergedCalcSessions = mergeById(workspace.calcMissionSessions, incoming.calcMissionSessions);
  const mergedProfile = mergeProfile(workspace.profile, {
    ...base.profile,
    ...incoming.profile,
    ...restoredProfile,
  });

  return {
    ...workspace,
    profile: mergedProfile,
    projects: mergedProjects,
    dealRooms: mergedDealRooms,
    treasuries: mergedTreasuries,
    calcMissionSessions: mergedCalcSessions,
    currentProjectId:
      !workspace.currentProjectId || isDemoOnlyWorkspace(workspace)
        ? incoming.currentProjectId || activeMergedProjects.find((project) => project.id !== DEMO_PROJECT_ID)?.id || activeMergedProjects[0]?.id
        : workspace.currentProjectId,
    currentCalcMissionSessionId:
      !workspace.currentCalcMissionSessionId || isDemoOnlyWorkspace(workspace)
        ? incoming.currentCalcMissionSessionId || mergedCalcSessions.find((session) => session.projectId !== DEMO_PROJECT_ID)?.id || mergedCalcSessions[0]?.id
        : workspace.currentCalcMissionSessionId,
    meta: {
      ...workspace.meta,
      updatedAt: Date.now(),
      migratedFrom: Array.from(new Set([...(workspace.meta.migratedFrom || []), `owner:${body}`])),
    },
  };
}
